import { zodResolver } from '@hookform/resolvers/zod';
import {
  billFromDispatcher, decimalsFor, dispatcherFromBill, lookupTableRate, rateDifferencePercent, roundDecimal, sameRate, sumMoney, toFixedDecimal, toScaled, trimRate,
} from '@mms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import { ApiError, api } from '../../api/client';
import type { Row } from '../../api/types';
import { useCan } from '../../auth/AuthContext';
import { invalidateData } from '../../crud/CrudScreen';
import { DateField, FormError, SelectField, applyServerErrors } from '../../forms/fields';
import { dateTimeReq, req, selectReq } from '../../forms/zod';
import { useBalance } from '../../ui/BalanceCard';
import { useConfirm, useToast } from '../../ui/feedback';
import { Amount, fromDateTimeInput, nowInput, toDateTimeInput } from '../../ui/format';
import { toOptions, useLatestRates, useLookup } from '../../ui/lookups';

/**
 * New / edit invoice (redesigned 2026-10-16). The invoice is raised against a party (Agent / Seller / Dispatcher) in the Bill currency, supplied by ONE
 * Dispatcher in its own currency. Each line has two amounts that follow each other through the line's rate:
 *     1 unit of the Bill currency = Rate units of the Dispatcher currency
 *     Dispatcher amount = Bill amount x Rate        Bill amount = Dispatcher amount / Rate
 * The one typed last is the anchor and is never overwritten. How it is posted is unchanged: the Dispatcher amount is `amount`, the Bill amount is the
 * generated `total` (amount / rate), `rate` is the rate used. The rate comes from the exchange-rate table (`lookupTableRate`) and may be edited.
 */
const PARTY_TYPES = [
  { value: 'agent', label: 'Agent', icon: 'person-badge' },
  { value: 'seller', label: 'Seller', icon: 'shop' },
  { value: 'dispatcher', label: 'Dispatcher', icon: 'truck' },
];

/** Dispatcher amounts are stored with 2 decimals (order_items.amount), whatever the currency; Bill amounts follow decimalsFor (OMR 3, others 2). */
const DISPATCHER_DP = 2;
/** A rate edited further than this many percent from the table rate shows a (non-blocking) warning. Set VITE_RATE_WARN_PERCENT to change it. */
export const RATE_WARN_PERCENT = Number(import.meta.env?.VITE_RATE_WARN_PERCENT ?? 1);

const strip = (v: string) => v.replace(/,/g, '').trim();
/** "1234567.5" -> "1,234,567.5" (display only; every parser strips the commas again). */
const group = (v: string) => v.replace(/^(\d+)/, (int) => int.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
const positiveDecimal = (msg: string) => z.string().refine((v) => /^\d+(\.\d+)?$/.test(strip(v)) && Number(strip(v)) > 0, msg);

const itemSchema = z.object({
  customerName: req('Enter the customer details'),
  billAmount: positiveDecimal('Enter the bill amount'),
  dispAmount: positiveDecimal('Enter the dispatcher amount'),
  rate: positiveDecimal('Enter a rate above 0'),
  /** the table rate this line was made against ('' when the table had none) */
  tableRate: z.string(),
  /** which amount was typed last: it is never overwritten by the calculation */
  anchor: z.enum(['bill', 'disp']),
});
const schema = z.object({
  currencyId: selectReq('Select a currency'),
  partyType: selectReq('Select a type'),
  partyId: selectReq('Select a party'),
  supplierCurrencyId: selectReq('Select the dispatcher currency'),
  supplierId: selectReq('Select a dispatcher'),
  transactionDate: dateTimeReq.refine((v) => !dayjs(v).isAfter(dayjs().add(5, 'minute')), 'Invoice date cannot be in the future'),
  remarks: z.string(),
  items: z.array(itemSchema).min(1, 'Add at least one line'),
});
type FormValues = z.infer<typeof schema>;
type Line = FormValues['items'][number];
const emptyLine = (rate = ''): Line => ({ customerName: '', billAmount: '', dispAmount: '', rate, tableRate: rate, anchor: 'bill' });

/**
 * Loads what the form needs BEFORE mounting it. The selects are uncontrolled, so a saved value can only
 * show if its <option> already exists when the form is created; mounting late would leave them blank
 * while the form state still held the ids. The rate table is loaded first too, so a line starts with its table rate.
 */
export function OrderEditorPage() {
  const { id } = useParams();
  const isNew = id === undefined;
  const existing = useQuery({ queryKey: ['order', id], queryFn: () => api.get<Row>(`/orders/${id}`), enabled: !isNew });
  const currencies = useLookup('currencies');
  const agents = useLookup('agents');
  const sellers = useLookup('purchasers');
  const dispatchers = useLookup('suppliers');
  const rates = useLatestRates();

  if (!isNew && existing.isError) {
    return <Alert variant="danger">{existing.error instanceof ApiError && existing.error.status === 404 ? 'Invoice not found' : 'Could not load the invoice'} <Link to="/orders">Back to invoices</Link></Alert>;
  }
  if ((!isNew && existing.isPending) || currencies.isPending || agents.isPending || sellers.isPending || dispatchers.isPending || rates.isPending) {
    return <div className="text-center py-5"><Spinner animation="border" role="status" aria-label="Loading" /></div>;
  }
  return <OrderForm key={id ?? 'new'} id={id} order={existing.data} />;
}

/** Does a party (seller/dispatcher lookup row) hold this currency? */
const holds = (p: Row, currencyId: string) => ((p.currencies as Row[] | undefined) ?? []).some((c) => String(c.currencyId) === currencyId);

function OrderForm({ id, order }: { id: string | undefined; order: Row | undefined }) {
  const isNew = id === undefined;
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  // Whoever may save the invoice may edit its rates (there is no separate "edit invoice rate" right yet).
  const canSave = useCan('orders', isNew ? 'create' : 'edit');
  const canEditRate = canSave;
  const [serverError, setServerError] = useState<string | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [rateFocus, setRateFocus] = useState<number | null>(null);
  const againRef = useRef(false); // the submit is a "Create and new"

  const currencies = useLookup('currencies');
  const agents = useLookup('agents');
  const sellers = useLookup('purchasers');
  const dispatchers = useLookup('suppliers');
  const rates = useLatestRates();
  // the base currency is the one flagged on the Currency table; the Exchange Rate table holds every other currency against it
  const baseId = currencies.data?.find((c) => c.isBase)?.id as number | undefined;
  const codeOf = (cid: string) => currencies.data?.find((c) => String(c.id) === cid)?.code as string | undefined;
  // decimal places of a currency: the Decimal places set on the Currency master (OMR 3, the others 2 until changed there)
  const decimalsOf = (cid: string) => { const c = currencies.data?.find((x) => String(x.id) === cid); return typeof c?.decimals === 'number' ? c.decimals : decimalsFor(c?.code as string | undefined); };
  const tableRateFor = (billId: string, dispId: string): string | null =>
    lookupTableRate((rates.data ?? []).map((r) => ({ currencyId: r.currencyId, rate1: r.rate1 })), baseId, Number(billId) || null, Number(dispId) || null);

  const initialValues = (): FormValues => {
    if (!order) return { currencyId: '', partyType: '', partyId: '', supplierCurrencyId: '', supplierId: '', transactionDate: nowInput(), remarks: '', items: [emptyLine()] };
    const billDp = decimalsOf(String(order.currencyId));
    return {
      currencyId: String(order.currencyId),
      partyType: String(order.partyType),
      partyId: String(order.partyId),
      supplierCurrencyId: String(order.supplierCurrencyId),
      supplierId: String(order.supplierId),
      transactionDate: toDateTimeInput(order.transactionDate),
      remarks: order.remarks ? String(order.remarks) : '',
      // Saved lines keep their rate exactly as booked and anchor on the Dispatcher amount (the stored one), so opening an invoice changes nothing.
      items: (order.items as Row[]).map((i) => ({
        customerName: String(i.customerName),
        billAmount: group(roundDecimal(String(i.total), billDp) ?? String(i.total)),
        dispAmount: group(roundDecimal(String(i.amount), DISPATCHER_DP) ?? String(i.amount)),
        rate: trimRate(String(i.rate)),
        tableRate: i.tableRate ? trimRate(String(i.tableRate)) : '',
        anchor: 'disp' as const,
      })),
    };
  };

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: initialValues() });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'items' });

  const currencyId = form.watch('currencyId');
  const partyType = form.watch('partyType');
  const partyId = form.watch('partyId');
  const supplierCurrencyId = form.watch('supplierCurrencyId');
  const supplierId = form.watch('supplierId');
  const items = form.watch('items');

  const billCode = codeOf(currencyId);
  const dispCode = codeOf(supplierCurrencyId);
  const billDp = decimalsOf(currencyId);
  const sameCurrency = !!currencyId && currencyId === supplierCurrencyId;
  const pairChosen = !!currencyId && !!supplierCurrencyId;
  const tableRate = pairChosen ? tableRateFor(currencyId, supplierCurrencyId) : null;
  const typeLabel = PARTY_TYPES.find((t) => t.value === partyType)?.label;

  // The party list: the chosen Type, limited to parties with an account in the chosen currency. The Dispatcher list works the same way.
  const partyRows: Row[] =
    !currencyId ? []
    : partyType === 'agent' ? (agents.data ?? []).filter((a) => holds(a, currencyId))
    : partyType === 'seller' ? (sellers.data ?? []).filter((p) => holds(p, currencyId))
    : partyType === 'dispatcher' ? (dispatchers.data ?? []).filter((p) => holds(p, currencyId))
    : [];
  const dispatcherRows: Row[] = supplierCurrencyId ? (dispatchers.data ?? []).filter((d) => holds(d, supplierCurrencyId)) : [];
  const partyPlaceholder = !currencyId ? 'Choose the currency first' : !partyType ? 'Choose the type first'
    : partyRows.length === 0 ? `No ${typeLabel?.toLowerCase()} has a ${billCode} account.` : 'Select…';
  const dispatcherPlaceholder = !supplierCurrencyId ? 'Choose the currency first'
    : dispatcherRows.length === 0 ? `No dispatcher has a ${dispCode} account.` : 'Select…';

  // Keep a selection only while it is still valid for its (new) currency.
  useEffect(() => {
    if (partyId && !partyRows.some((p) => String(p.id) === partyId)) form.setValue('partyId', '');
  }, [currencyId, partyType, partyId, agents.data, sellers.data, dispatchers.data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (supplierId && !dispatcherRows.some((d) => String(d.id) === supplierId)) form.setValue('supplierId', '');
  }, [supplierCurrencyId, supplierId, dispatchers.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- the two-way calculation: the anchor amount stays, the other follows the rate
  const recalc = (i: number) => {
    const l = form.getValues(`items.${i}`);
    if (l.anchor === 'bill') {
      const d = dispatcherFromBill(strip(l.billAmount), strip(l.rate), DISPATCHER_DP);
      form.setValue(`items.${i}.dispAmount`, d ? group(d) : '', { shouldValidate: form.formState.isSubmitted });
    } else {
      const b = billFromDispatcher(strip(l.dispAmount), strip(l.rate), billDp);
      form.setValue(`items.${i}.billAmount`, b ? group(b) : '', { shouldValidate: form.formState.isSubmitted });
    }
  };
  const onAmountChange = (i: number, anchor: 'bill' | 'disp') => {
    form.setValue(`items.${i}.anchor`, anchor);
    recalc(i);
  };
  // the amount that was typed is shown rounded (and with thousands separators) once the field is left
  const onAmountBlur = (i: number, anchor: 'bill' | 'disp') => {
    const name = anchor === 'bill' ? (`items.${i}.billAmount` as const) : (`items.${i}.dispAmount` as const);
    const r = roundDecimal(strip(form.getValues(name)), anchor === 'bill' ? billDp : DISPATCHER_DP);
    if (r && Number(r) > 0) form.setValue(name, group(r));
  };

  // A currency changed: every line goes back to the table rate for the new pair (edited rates are discarded) and its other amount is recalculated.
  const pair = `${currencyId}|${supplierCurrencyId}`;
  const lastPair = useRef(pair);
  useEffect(() => {
    if (lastPair.current === pair) return;
    lastPair.current = pair;
    const t = pairChosen ? tableRateFor(currencyId, supplierCurrencyId) : null;
    form.getValues('items').forEach((_, i) => {
      form.setValue(`items.${i}.rate`, t ?? '');
      form.setValue(`items.${i}.tableRate`, t ?? '');
      recalc(i);
    });
  }, [pair]); // eslint-disable-line react-hooks/exhaustive-deps

  // The swap button: the currencies change places, each line's Bill amount becomes its old Dispatcher amount, and the new direction's rate is fetched
  // (by the effect above), which recalculates the Dispatcher amount.
  const swap = () => {
    const lines = form.getValues('items').map((l) => ({ ...l, billAmount: l.dispAmount, anchor: 'bill' as const }));
    form.setValue('items', lines);
    const c = form.getValues('currencyId');
    form.setValue('currencyId', form.getValues('supplierCurrencyId'), { shouldDirty: true });
    form.setValue('supplierCurrencyId', c, { shouldDirty: true });
  };

  const addLine = () => {
    const n = form.getValues('items').length;
    append(emptyLine(tableRate ?? ''));
    setTimeout(() => form.setFocus(`items.${n}.customerName`), 0);
  };
  // copies the customer, both amounts, the rate (edited or not, with its table rate) and which amount was typed last
  const duplicateLast = () => {
    const last = form.getValues('items').at(-1);
    if (last) append({ ...last });
  };

  // ---- totals and the Dispatcher's balance
  const validLines = items.filter((l) => Number(strip(l.billAmount)) > 0 && Number(strip(l.dispAmount)) > 0);
  const billTotal = sumMoney(validLines.map((l) => strip(l.billAmount)), billDp);
  const dispTotal = sumMoney(validLines.map((l) => strip(l.dispAmount)), DISPATCHER_DP);
  const dispBal = useBalance('suppliers', supplierId, supplierCurrencyId);
  // an invoice being edited already counts in the balance, so what it took is available again
  const restored = order && String(order.supplierId) === supplierId && String(order.supplierCurrencyId) === supplierCurrencyId
    ? sumMoney((order.items as Row[]).map((i) => String(i.amount)), 3) : '0.000';
  const available = dispBal.data ? sumMoney([dispBal.data.balance, restored], 3) : null;
  const afterRaw = available && dispTotal !== '0.00' ? sumMoney([available, `-${dispTotal}`], 3) : null;
  const after = afterRaw ? (afterRaw.startsWith('-') ? '0.000' : afterRaw) : null;
  const overBalance = available !== null && (toScaled(dispTotal, 3) ?? 0n) > (toScaled(available, 3) ?? 0n);
  const dispatcherName = dispatcherRows.find((d) => String(d.id) === supplierId)?.name as string | undefined;
  // the dispatcher's Low balance alert for this currency (Dispatcher master): warn before more money goes through a dispatcher that needs topping up
  const alertLevel = Number(((dispatchers.data ?? []).find((d) => String(d.id) === supplierId)?.currencies as Row[] | undefined)?.find((c) => String(c.currencyId) === supplierCurrencyId)?.lowBalanceAlert ?? 0);
  const lowNow = alertLevel > 0 && available !== null && Number(available) >= 0 && Number(available) < alertLevel;
  const lowAfter = !lowNow && alertLevel > 0 && after !== null && Number(after) < alertLevel;
  const lowText = lowNow
    ? `${dispatcherName} holds ${dispCode} ${group(roundDecimal(available!, 2) ?? '')}, below its alert of ${group(roundDecimal(String(alertLevel), 2) ?? '')}`
    : lowAfter ? `After this invoice ${dispatcherName} would hold ${dispCode} ${group(roundDecimal(after!, 2) ?? '')}, below its alert of ${group(roundDecimal(String(alertLevel), 2) ?? '')}` : null;

  const save = useMutation({
    mutationFn: (v: FormValues) => {
      const payload = {
        currencyId: Number(v.currencyId),
        partyType: v.partyType,
        partyId: Number(v.partyId),
        supplierId: Number(v.supplierId),
        supplierCurrencyId: Number(v.supplierCurrencyId),
        transactionDate: fromDateTimeInput(v.transactionDate),
        remarks: v.remarks.trim() || null,
        items: v.items.map((i) => ({
          customerName: i.customerName.trim(),
          amount: roundDecimal(strip(i.dispAmount), DISPATCHER_DP) ?? strip(i.dispAmount),
          rate: strip(i.rate),
          tableRate: i.tableRate ? i.tableRate : null,
        })),
      };
      return isNew ? api.post<Row>('/orders', payload) : api.put<Row>(`/orders/${id}`, payload);
    },
    onSuccess: async (o, v) => {
      await invalidateData(qc);
      const total = `${group(roundDecimal(sumMoney(v.items.map((i) => strip(i.dispAmount)), DISPATCHER_DP), DISPATCHER_DP) ?? '')} ${codeOf(v.supplierCurrencyId) ?? ''}`.trim();
      toast.success(isNew ? `Invoice ${o.transactionNo} created. Dispatcher total ${total}` : `Invoice ${o.transactionNo} updated. Dispatcher total ${total}`);
      if (isNew && againRef.current) {
        againRef.current = false;
        // "Create and new": the lines go, the currencies, Type, Party and Dispatcher stay
        form.reset({ ...v, transactionDate: nowInput(), remarks: '', items: [emptyLine(tableRate ?? '')] });
        window.scrollTo?.({ top: 0 });
        return;
      }
      nav('/orders');
    },
    onError: (e) => setServerError(applyServerErrors(form, e)),
  });

  const submit = (v: FormValues) => {
    setServerError(null);
    if (overBalance) { setBalanceError(`Total is more than ${dispatcherName ?? 'the dispatcher'} holds`); return; }
    setBalanceError(null);
    save.mutate(v);
  };

  const err = (i: number, k: keyof Line) => (form.formState.errors.items?.[i]?.[k] as { message?: string } | undefined)?.message;
  const itemsError = form.formState.errors.items?.root?.message ?? (form.formState.errors.items as { message?: string } | undefined)?.message;
  const headerErrors = (['currencyId', 'partyType', 'partyId', 'supplierCurrencyId', 'supplierId', 'transactionDate'] as const).filter((k) => form.formState.errors[k]).length;
  const badLines = Array.isArray(form.formState.errors.items) ? form.formState.errors.items.filter(Boolean).length : 0;
  const ro = !canSave;

  const hasContent = items.some((l) => l.customerName.trim() || l.billAmount.trim() || l.dispAmount.trim());
  const goBack = async () => {
    const unsaved = isNew ? hasContent : form.formState.isDirty;
    if (!unsaved || ro || (await confirm({ title: 'Leave this invoice?', message: 'You have unsaved lines. Leave without saving?', confirmLabel: 'Leave', danger: true }))) nav('/orders');
  };

  const rateHead = pairChosen ? `1 ${billCode} = ? ${dispCode}` : '1 CUR1 = ? CUR2';
  const noTableRate = pairChosen && tableRate === null;
  const warnLines = items
    .map((l, i) => ({ i, pct: l.tableRate ? rateDifferencePercent(strip(l.rate), l.tableRate) : null }))
    .filter((x) => x.pct !== null && x.pct > RATE_WARN_PERCENT);

  const lineInput = (i: number, k: 'customerName' | 'billAmount' | 'dispAmount', anchor?: 'bill' | 'disp') => (
    <>
      <Form.Control
        size="sm" autoComplete="off" disabled={ro} isInvalid={!!err(i, k)}
        className={k === 'customerName' ? '' : 'text-end'}
        inputMode={k === 'customerName' ? undefined : 'decimal'}
        placeholder={k === 'customerName' ? 'Customer name or ID' : '0.00'}
        aria-label={`Line ${i + 1} ${k === 'customerName' ? 'customer' : k === 'billAmount' ? 'bill amount' : 'dispatcher amount'}`}
        {...form.register(`items.${i}.${k}`, anchor ? { onChange: () => onAmountChange(i, anchor), onBlur: () => onAmountBlur(i, anchor) } : undefined)}
      />
      <Form.Control.Feedback type="invalid">{err(i, k)}</Form.Control.Feedback>
    </>
  );
  const rateInput = (i: number) => {
    const l = items[i];
    const edited = !!l && l.tableRate !== '' && l.rate !== '' && !sameRate(strip(l.rate), l.tableRate);
    return (
      <>
        <Form.Control
          size="sm" className={`text-end ${edited ? 'border-warning invoice-rate-edited' : ''}`} inputMode="decimal" autoComplete="off" aria-label={`Line ${i + 1} rate`}
          disabled={ro || !canEditRate || sameCurrency} readOnly={!canEditRate} isInvalid={!!err(i, 'rate')}
          title={edited ? `Edited. Table rate is ${toFixedDecimal(l!.tableRate, 4)}` : undefined}
          value={rateFocus === i ? (l?.rate ?? '') : (l?.rate ? toFixedDecimal(strip(l.rate), 4) : '')}
          onFocus={() => setRateFocus(i)}
          onBlur={() => setRateFocus(null)}
          onChange={(e) => { form.setValue(`items.${i}.rate`, e.target.value, { shouldDirty: true, shouldValidate: form.formState.isSubmitted }); recalc(i); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && i === fields.length - 1 && !ro) { e.preventDefault(); addLine(); } }}
        />
        <Form.Control.Feedback type="invalid">{err(i, 'rate')}</Form.Control.Feedback>
      </>
    );
  };

  return (
    <Form noValidate onSubmit={form.handleSubmit(submit)}>
      {/* header: the title with the invoice number; Date & time and Back on the right */}
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
        <div>
          <h1 className="h4 mb-0 d-flex align-items-center flex-wrap gap-2">
            <i className="bi bi-cart text-primary" aria-hidden />{isNew ? 'New invoice' : 'Invoice'}
            {order?.transactionNo ? <small className="text-body-secondary fs-6">{String(order.transactionNo)}</small> : null}
          </h1>
          {!isNew && <div className="text-body-secondary small">{ro ? 'Read-only: you can view but not edit invoices' : 'Saving replaces the lines below'}</div>}
        </div>
        <div className="d-flex align-items-end gap-2">
          <div style={{ minWidth: 230 }}><DateField form={form} name="transactionDate" label="Date & time" mode="datetime" required disabled={ro} className="mb-0" /></div>
          {!isNew && <Button variant="outline-secondary" className="no-print" onClick={() => window.print()}><i className="bi bi-printer me-1" aria-hidden />Print</Button>}
          <Button variant="outline-secondary" className="no-print" onClick={goBack}>Back</Button>
        </div>
      </div>
      <FormError message={serverError} />

      {/* 1 Bill to  [swap]  2 Supplied by (stacked below lg, the swap button between the two) */}
      <div className="row g-3 align-items-stretch mb-3">
        <div className="col-12 col-lg">
          <div className="card h-100" data-testid="left-pane">
            <div className="card-header fw-semibold"><span className="badge rounded-pill text-bg-primary me-2">1</span>Bill to</div>
            <div className="card-body">
              <div className="mb-3">
                <div className="form-label">Type <span className="text-danger">*</span></div>
                <div className="btn-group w-100" role="radiogroup" aria-label="Type">
                  {PARTY_TYPES.map((t) => (
                    <Button key={t.value} type="button" role="radio" aria-checked={partyType === t.value} disabled={ro}
                      variant={partyType === t.value ? 'primary' : 'outline-secondary'}
                      onClick={() => form.setValue('partyType', t.value, { shouldValidate: form.formState.isSubmitted, shouldDirty: true })}>
                      <i className={`bi bi-${t.icon} me-1`} aria-hidden />{t.label}
                    </Button>
                  ))}
                </div>
                {form.formState.errors.partyType && <div className="text-danger small mt-1">{form.formState.errors.partyType.message}</div>}
              </div>
              <div className="row">
                <div className="col-sm-6">
                  <SelectField form={form} name="currencyId" label="Currency" required disabled={ro}
                    options={toOptions(currencies.data, (c) => c.code as string)} />
                </div>
                <div className="col-sm-6">
                  <SelectField form={form} name="partyId" label={typeLabel ?? 'Party'} required disabled={ro || !currencyId || !partyType}
                    placeholder={partyPlaceholder} options={toOptions(partyRows)} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-auto d-flex align-items-center justify-content-center">
          <Button type="button" variant="outline-secondary" className="rounded-circle" onClick={swap} disabled={ro} aria-label="Swap currencies" title="Swap the Currency and the Dispatcher currency">
            <i className="bi bi-arrow-left-right" aria-hidden />
          </Button>
        </div>
        <div className="col-12 col-lg">
          <div className="card h-100" data-testid="right-pane">
            <div className="card-header fw-semibold"><span className="badge rounded-pill text-bg-primary me-2">2</span>Supplied by</div>
            <div className="card-body">
              <div className="mb-3" data-testid="dispatcher-holds">
                <div className="form-label">Dispatcher holds</div>
                <div className="form-control d-flex justify-content-between align-items-center bg-body-tertiary">
                  {supplierId && dispBal.data ? (
                    <>
                      <span><Amount value={available ?? dispBal.data.balance} code={dispCode} decimals={2} /></span>
                      {after && <span><i className="bi bi-arrow-right me-1" aria-hidden /><strong data-testid="holds-after"><Amount value={after} code={dispCode} decimals={2} /></strong></span>}
                    </>
                  ) : <span aria-label="nothing selected">—</span>}
                </div>
                {lowText && <div className="small text-warning mt-1" role="status" data-testid="low-balance-warning"><i className="bi bi-exclamation-triangle me-1" aria-hidden />{lowText}</div>}
              </div>
              <div className="row">
                <div className="col-sm-6">
                  <SelectField form={form} name="supplierCurrencyId" label="Dispatcher currency" required disabled={ro}
                    options={toOptions(currencies.data, (c) => c.code as string)} />
                </div>
                <div className="col-sm-6">
                  <SelectField form={form} name="supplierId" label="Dispatcher" required disabled={ro || !supplierCurrencyId}
                    placeholder={dispatcherPlaceholder} options={toOptions(dispatcherRows)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="table-responsive">
          <Table className="mb-0 invoice-lines align-middle" size="sm">
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th style={{ minWidth: 180 }}>Customer details</th>
                <th className="text-end" style={{ minWidth: 150 }}>Bill amount{billCode && <> <small className="text-body-secondary">({billCode})</small></>}</th>
                <th style={{ width: 28 }}><span className="visually-hidden">Both ways</span></th>
                <th className="text-end" style={{ minWidth: 150 }}>Dispatcher amount{dispCode && <> <small className="text-body-secondary">({dispCode})</small></>}</th>
                <th className="text-end" style={{ minWidth: 120 }}>Rate<div className="small fw-normal text-body-secondary" data-testid="rate-hint">{rateHead}</div></th>
                {!ro && <th style={{ width: 44 }}><span className="visually-hidden">Remove</span></th>}
              </tr>
            </thead>
            <tbody>
              {fields.map((f, i) => (
                <tr key={f.id}>
                  <td className="text-body-secondary">{i + 1}</td>
                  <td>{lineInput(i, 'customerName')}</td>
                  <td>{lineInput(i, 'billAmount', 'bill')}</td>
                  <td className="text-center text-body-secondary"><i className="bi bi-arrow-left-right" aria-hidden /></td>
                  <td>{lineInput(i, 'dispAmount', 'disp')}</td>
                  <td>{rateInput(i)}</td>
                  {!ro && (
                    <td>
                      <Button size="sm" variant="outline-secondary" className="invoice-remove" aria-label={`Remove line ${i + 1}`} disabled={fields.length === 1} onClick={() => remove(i)}>
                        <i className="bi bi-trash" aria-hidden />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        {itemsError && <div className="text-danger small px-3 py-2" role="alert">{itemsError}</div>}
        {noTableRate && <div className="text-danger small px-3 py-2" role="alert" data-testid="no-table-rate">No table rate for {billCode} to {dispCode}. Enter a rate on each line.</div>}
        {warnLines.length > 0 && (
          <div className="text-warning small px-3 py-2" role="status" data-testid="rate-warning">
            {warnLines.map((x) => <div key={x.i}>Line {x.i + 1}: the rate is {x.pct!.toFixed(2)}% away from the table rate.</div>)}
          </div>
        )}
        {!ro && (
          <div className="card-footer">
            <div className="d-flex flex-wrap gap-2 mb-2">
              <Button size="sm" variant="outline-secondary" onClick={addLine} disabled={fields.length >= 200}>
                <i className="bi bi-plus-lg me-1" aria-hidden />Add line
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={duplicateLast} disabled={fields.length >= 200}>
                <i className="bi bi-files me-1" aria-hidden />Duplicate last line
              </Button>
            </div>
            <div className="small text-body-secondary">Rate is filled from the rate table and can be edited. Type in either amount, the other updates.</div>
          </div>
        )}
      </div>

      <div className="mb-3">
        <Form.Label htmlFor="invoice-remarks">Remarks (optional)</Form.Label>
        <Form.Control id="invoice-remarks" as="textarea" rows={2} disabled={ro} {...form.register('remarks')} />
      </div>

      <div className="sticky-bottom invoice-summary-bar bg-body border rounded shadow-sm p-3 d-flex flex-wrap align-items-center gap-4 no-print">
        <div>
          <div className="small text-body-secondary">Lines</div>
          <strong data-testid="bar-lines">{items.length} {items.length === 1 ? 'line' : 'lines'}</strong>
        </div>
        <div>
          <div className="small text-body-secondary">Bill amount{billCode ? ` (${billCode})` : ''}</div>
          <strong data-testid="bar-bill"><Amount value={billTotal} decimals={billDp} /></strong>
        </div>
        <div>
          <div className="small text-body-secondary">Dispatcher amount{dispCode ? ` (${dispCode})` : ''}</div>
          <strong className="fs-4" data-testid="bar-total"><Amount value={dispTotal} decimals={DISPATCHER_DP} /></strong>
        </div>
        <div className="flex-grow-1 small" role="alert" data-testid="footer-error">
          {balanceError ? <span className="text-danger">{balanceError}</span>
            : form.formState.isSubmitted && (badLines > 0 || headerErrors > 0)
              ? <span className="text-danger">{badLines > 0 ? `Fix ${badLines} highlighted ${badLines === 1 ? 'line' : 'lines'}` : 'Fix the highlighted fields'}</span>
              : null}
        </div>
        {!ro && (
          <div className="d-flex gap-2">
            {isNew && <Button type="submit" variant="outline-secondary" disabled={save.isPending} onClick={() => { againRef.current = true; }}>Create and new</Button>}
            <Button type="submit" disabled={save.isPending} onClick={() => { againRef.current = false; }}>{save.isPending ? 'Saving…' : isNew ? 'Create invoice' : 'Save changes'}</Button>
          </div>
        )}
      </div>
    </Form>
  );
}
